import type { Project, Track } from '@/domain/types';
import { withTrack } from '@/domain/mutate';
import {
  DEFAULT_LOOP_CROSSFADE_SEC,
  MAX_LOOP_CROSSFADE_SEC,
  MAX_ZONES,
  autoAssignRanges,
  readSamplerZones,
  repairLoopPoints,
  writeSamplerZones,
  type SamplerZone,
} from '@/engine/instruments/samplerState';
import { newAssetId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, idParam, intParam, numberParam, stringParam } from './params';

/**
 * Sampler zone editing.
 *
 * Zones live inside the track's instrument state, so they are saved, packaged,
 * migrated and undone by the machinery that already exists — there is no
 * separate sampler document to keep in step with the project.
 */

function zoneId(): string {
  // Zones are addressed like any other entity; the prefix keeps ids readable
  // in a saved project.
  return newAssetId().replace('ast_', 'zon_');
}

function updateZones(
  project: Project,
  trackId: string,
  update: (zones: SamplerZone[], track: Track) => SamplerZone[] | null,
): Project {
  return withTrack(project, trackId, (track) => {
    const instrument = track.instrument;
    if (!instrument || instrument.type !== 'sampler') return track;
    const next = update(readSamplerZones(instrument.data), track);
    if (!next) return track;
    return { ...track, instrument: writeSamplerZones(instrument, next) };
  });
}

export const addSamplerZone = defineCommand<{
  trackId: string;
  zoneId: string;
  assetId: string;
  name: string;
  rootPitch: number;
  offsetSec: number;
  durationSec: number;
  autoRange: boolean;
}>({
  type: 'sampler.addZone',
  label: (p) => `Add "${p.name}" to the sampler`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const assetId = idParam(raw.assetId);
    const id = raw.zoneId === undefined ? zoneId() : idParam(raw.zoneId);
    const rootPitch = intParam(raw.rootPitch, 0, 127, 60);
    const durationSec = numberParam(raw.durationSec, 0, 86400);
    if (!trackId || !assetId || !id || rootPitch === null || durationSec === null) return null;
    return {
      trackId,
      zoneId: id,
      assetId,
      name: stringParam(raw.name, 128, 'Sample') ?? 'Sample',
      rootPitch,
      offsetSec: numberParam(raw.offsetSec, 0, 86400, 0) ?? 0,
      durationSec,
      autoRange: boolParam(raw.autoRange, true) ?? true,
    };
  },
  apply: (project, p) =>
    updateZones(project, p.trackId, (zones) => {
      if (zones.length >= MAX_ZONES) return null;
      if (zones.some((zone) => zone.id === p.zoneId)) return null;
      if (!project.assets[p.assetId]) return null;

      const zone: SamplerZone = {
        id: p.zoneId,
        assetId: p.assetId,
        name: p.name,
        rootPitch: p.rootPitch,
        lowPitch: p.rootPitch,
        highPitch: p.rootPitch,
        offsetSec: p.offsetSec,
        durationSec: p.durationSec,
        lowVelocity: 1,
        highVelocity: 127,
        roundRobinGroup: 0,
        chokeGroup: 0,
        gain: 1,
        tuneCents: 0,
        loop: false,
        loopStartSec: p.offsetSec,
        loopEndSec: p.offsetSec + p.durationSec,
        loopCrossfadeSec: DEFAULT_LOOP_CROSSFADE_SEC,
        oneShot: false,
        keyTracking: true,
        reverse: false,
      };

      const next = [...zones, zone];
      // Without this a new zone would answer to exactly one key, which is not
      // what dropping a sample onto a sampler is meant to produce.
      return p.autoRange ? autoAssignRanges(next) : next;
    }),
});

export const updateSamplerZone = defineCommand<{
  trackId: string;
  zoneId: string;
  patch: Partial<
    Pick<
      SamplerZone,
      | 'rootPitch'
      | 'lowPitch'
      | 'highPitch'
      | 'lowVelocity'
      | 'highVelocity'
      | 'roundRobinGroup'
      | 'chokeGroup'
      | 'gain'
      | 'tuneCents'
      | 'loop'
      | 'loopStartSec'
      | 'loopEndSec'
      | 'loopCrossfadeSec'
      | 'oneShot'
      | 'keyTracking'
      | 'reverse'
      | 'offsetSec'
      | 'durationSec'
      | 'name'
    >
  >;
}>({
  type: 'sampler.updateZone',
  label: () => 'Edit sampler zone',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const id = idParam(raw.zoneId);
    if (!trackId || !id) return null;

    const patch: Record<string, unknown> = {};
    if (raw.rootPitch !== undefined) patch.rootPitch = intParam(raw.rootPitch, 0, 127);
    if (raw.lowPitch !== undefined) patch.lowPitch = intParam(raw.lowPitch, 0, 127);
    if (raw.highPitch !== undefined) patch.highPitch = intParam(raw.highPitch, 0, 127);
    if (raw.lowVelocity !== undefined) patch.lowVelocity = intParam(raw.lowVelocity, 1, 127);
    if (raw.highVelocity !== undefined) patch.highVelocity = intParam(raw.highVelocity, 1, 127);
    if (raw.roundRobinGroup !== undefined) patch.roundRobinGroup = intParam(raw.roundRobinGroup, 0, 64);
    if (raw.chokeGroup !== undefined) patch.chokeGroup = intParam(raw.chokeGroup, 0, 64);
    if (raw.gain !== undefined) patch.gain = numberParam(raw.gain, 0, 4);
    if (raw.tuneCents !== undefined) patch.tuneCents = numberParam(raw.tuneCents, -1200, 1200);
    if (raw.offsetSec !== undefined) patch.offsetSec = numberParam(raw.offsetSec, 0, 86400);
    if (raw.durationSec !== undefined) patch.durationSec = numberParam(raw.durationSec, 0, 86400);
    if (raw.loop !== undefined) patch.loop = boolParam(raw.loop);
    if (raw.oneShot !== undefined) patch.oneShot = boolParam(raw.oneShot);
    if (raw.keyTracking !== undefined) patch.keyTracking = boolParam(raw.keyTracking);
    if (raw.loopStartSec !== undefined) patch.loopStartSec = numberParam(raw.loopStartSec, 0, 86400);
    if (raw.loopEndSec !== undefined) patch.loopEndSec = numberParam(raw.loopEndSec, 0, 86400);
    if (raw.loopCrossfadeSec !== undefined) patch.loopCrossfadeSec = numberParam(raw.loopCrossfadeSec, 0, MAX_LOOP_CROSSFADE_SEC);
    if (raw.name !== undefined) patch.name = stringParam(raw.name, 128);
    if (raw.reverse !== undefined) patch.reverse = boolParam(raw.reverse);

    // A patch containing an unreadable value is rejected outright rather than
    // silently applying the half of it that parsed.
    if (Object.values(patch).some((value) => value === null)) return null;
    if (Object.keys(patch).length === 0) return null;

    return { trackId, zoneId: id, patch: patch as never };
  },
  coalesceKey: (p) => `zone:${p.trackId}:${p.zoneId}:${Object.keys(p.patch).join(',')}`,
  apply: (project, p) =>
    updateZones(project, p.trackId, (zones) => {
      if (!zones.some((zone) => zone.id === p.zoneId)) return null;
      return zones.map((zone) => {
        if (zone.id !== p.zoneId) return zone;
        const next = { ...zone, ...p.patch };
        // A zone loops or plays out, never both: whichever was just switched
        // on wins, and switching one on switches the other off.
        if (next.loop && next.oneShot) {
          if (p.patch.oneShot && !p.patch.loop) next.loop = false;
          else next.oneShot = false;
        }
        // Keep both ranges coherent whichever end was dragged, and the loop
        // points inside the region whichever of the two was moved.
        return {
          ...next,
          lowPitch: Math.min(next.lowPitch, next.highPitch),
          highPitch: Math.max(next.lowPitch, next.highPitch),
          lowVelocity: Math.min(next.lowVelocity, next.highVelocity),
          highVelocity: Math.max(next.lowVelocity, next.highVelocity),
          ...repairLoopPoints(next, zone),
        };
      });
    }),
});

export const removeSamplerZone = defineCommand<{ trackId: string; zoneId: string }>({
  type: 'sampler.removeZone',
  label: () => 'Remove sampler zone',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const id = idParam(raw.zoneId);
    return trackId && id ? { trackId, zoneId: id } : null;
  },
  apply: (project, p) =>
    updateZones(project, p.trackId, (zones) => {
      const next = zones.filter((zone) => zone.id !== p.zoneId);
      return next.length === zones.length ? null : next;
    }),
});

export const autoMapSampler = defineCommand<{ trackId: string }>({
  type: 'sampler.autoMap',
  label: () => 'Spread zones across the keyboard',
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    return trackId ? { trackId } : null;
  },
  apply: (project, p) => updateZones(project, p.trackId, (zones) => (zones.length === 0 ? null : autoAssignRanges(zones))),
});
